import { Button } from '@bambi/component-library/src/components/Button/Button';
import { DefaultFormFieldLayout } from '@bambi/component-library/src/components/forms/DefaultFormFieldLayout/DefaultFormFieldLayout';
import { CoreFormFieldTime } from '@bambi/component-library/src/components/forms/fields/CoreFormFieldTime';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import {
  TripLocationDetail,
  TripLocationDetailRead,
} from 'src/common/external/bambi-api/bambiApi';
import { CoreFormFieldLocationTypeahead } from 'src/common/FormField/v2/core/CoreFormFieldLocationTypeahead';
import { SpaceTypeField } from 'src/common/SpaceTypeField';
import { removeUnitedStatesFromAddress } from 'src/common/util/removeUnitedStatesFromAddress';
import { ExistingPassengerSearch } from 'src/features/add-trip/PassengerInfoFormSection/ExistingPassengerSearch';
import { AdditionalPassengersContextField } from 'src/features/add-trip/ServiceDetails/ServiceDetailsFormBody/AdditionalPassengersContextField';

import { RouteTripTemplateFormValues } from './types';

interface RouteTemplateTripMatcherFormProps {
  onCancel: () => void;
  onSubmit: SubmitHandler<RouteTripTemplateFormValues>;
  values?: Partial<RouteTripTemplateFormValues>;
}
export function RouteTemplateTripMatcherForm({
  onCancel,
  onSubmit,
  values,
}: RouteTemplateTripMatcherFormProps) {
  const form = useForm<RouteTripTemplateFormValues>({
    defaultValues: {
      ...(values ?? {}),
    },
    mode: 'all',
  });

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-4" data-testid="trip-matcher-form">
        <Controller
          control={form.control}
          name="passenger"
          render={({ field }) => {
            return (
              <DefaultFormFieldLayout label="Passenger *">
                <ExistingPassengerSearch
                  selectedPassenger={field.value}
                  onPassengerSelected={(passenger) => {
                    if (passenger) {
                      form.setValue('passenger', passenger);
                      form.setValue('passenger_id', passenger.id);
                    }
                  }}
                  allowAddPassenger={false}
                />
              </DefaultFormFieldLayout>
            );
          }}
        />
        <div className="flex flex-row justify-between gap-2">
          <Controller
            control={form.control}
            name="pickup"
            rules={{ required: 'This field is required' }}
            render={({ field, fieldState }) => {
              const value = field.value as TripLocationDetailRead | undefined;
              return (
                <div className="w-full">
                  <CoreFormFieldLocationTypeahead
                    label="Pickup Location *"
                    value={
                      value
                        ? {
                            label: value.address_details ?? '',
                            value: value.place_id,
                          }
                        : undefined
                    }
                    inputProps={{
                      ...field,
                      value: value?.place_id ?? undefined,
                    }}
                    onChange={(option) =>
                      field.onChange({
                        place_id: option.value,
                        address: removeUnitedStatesFromAddress(option.label),
                        address_details: removeUnitedStatesFromAddress(
                          option.label
                        ),
                        id: `${uuid()}-new`,
                      })
                    }
                    error={fieldState.error?.message}
                  />
                </div>
              );
            }}
          />
          <Controller
            control={form.control}
            name="scheduled_pickup_time"
            rules={{ required: 'This field is required' }}
            render={({ field, fieldState }) => {
              return (
                <CoreFormFieldTime
                  inputProps={{
                    ...field,
                    style: { width: '125px' },
                  }}
                  label="Pickup Time *"
                  error={fieldState.error?.message}
                />
              );
            }}
          />
        </div>
        <Controller
          control={form.control}
          name="dropoff"
          rules={{ required: 'This field is required' }}
          render={({ field, fieldState }) => {
            const value = field.value as TripLocationDetail | undefined;
            return (
              <div className="w-full">
                <CoreFormFieldLocationTypeahead
                  label="Dropoff Location *"
                  value={
                    value
                      ? {
                          label: value.address_details ?? '',
                          value: value.place_id,
                        }
                      : undefined
                  }
                  inputProps={{
                    ...field,
                    value: value?.place_id ?? undefined,
                  }}
                  onChange={(option) =>
                    field.onChange({
                      place_id: option.value,
                      address: removeUnitedStatesFromAddress(option.label),
                      address_details: removeUnitedStatesFromAddress(
                        option.label
                      ),
                      id: `${uuid()}-new`,
                    })
                  }
                  error={fieldState.error?.message}
                />
              </div>
            );
          }}
        />
        <div className="flex flex-row items-center justify-between">
          <SpaceTypeField
            fieldName="space_type"
            label="Space Type *"
            rules={{ required: 'This field is required' }}
          />
          <AdditionalPassengersContextField fieldName="num_additional_passengers" />
        </div>
        <div className="mt-2 flex flex-row justify-between">
          <Button onClick={onCancel}>Back</Button>
          <Button
            variant="primary"
            onClick={form.handleSubmit(onSubmit)}
            dataTestId="update-matcher-button"
          >
            {values ? 'Update' : 'Add'} Matcher
          </Button>
        </div>
      </div>
    </FormProvider>
  );
}
